import { type } from 'arktype';
import { isoDateString } from './labour-pool-claim.model';
import { rruleSchema } from '@zag/common/validity';

// ---------------------------------------------------------------------------
// Available days specification
// ---------------------------------------------------------------------------

/** Specifies which days within a date range an employee is available as a floater */
export interface LabourPoolOfferAvailableDays {
  /** ISO weekday numbers (1=Mon … 7=Sun). Absent means all weekdays apply. */
  weekdays?: number[];
  /** Specific ISO dates (YYYY-MM-DD) to override or restrict weekday matching */
  specificDates?: string[];
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export const labourPoolOfferStatuses = ['active', 'partially_claimed', 'fully_claimed', 'withdrawn', 'expired'] as const;
export type LabourPoolOfferStatus = (typeof labourPoolOfferStatuses)[number];

// ---------------------------------------------------------------------------
// Domain type — returned by the API
// ---------------------------------------------------------------------------

export interface LabourPoolOffer {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeProfilePicture: string | null;
  skillIds: string[];
  offeringTeamId: string;
  offeringTeamName: string;
  offeredBy: string;
  offeredByName: string;
  startDate: string;
  endDate: string;
  rrule: string | null;
  availableDays: LabourPoolOfferAvailableDays | null;
  /** Minimum claim duration in days for a flexible-window offer. `null` for fixed-window offers (paired with `maxDurationDays`). */
  minDurationDays: number | null;
  /** Maximum claim duration in days for a flexible-window offer. `null` for fixed-window offers (paired with `minDurationDays`). */
  maxDurationDays: number | null;
  /**
   * Story 23-11: when false, the offer must be claimed as a single block covering the
   * full eligible window. Continuity-critical lending (handover, mentoring). Default true.
   */
  isSplittable: boolean;
  notes: string | null;
  /** Computed: whether this offer can currently be claimed */
  isActive: boolean;
  status: LabourPoolOfferStatus;
  /** Number of approved claims consuming this offer */
  claimedCount: number;
  /** Whether the offer still has unclaimed capacity (single-employee offers: 0 or 1) */
  isClaimable: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Create schema — validated on POST /api/v1/labour-pool/offers
// ---------------------------------------------------------------------------

export const createLabourPoolOfferSchema = type({
  employeeId: type('string').configure({
    description: 'ID of the employee being offered to the Labour Pool.',
    examples: ['employee_abc123'],
  }),
  offeringTeamId: type('string').configure({
    description: "ID of the team offering the employee (the employee's home team).",
    examples: ['team_abc123'],
  }),
  startDate: isoDateString.configure({
    description: 'Inclusive start of the offer window as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  endDate: isoDateString.configure({
    description: 'Inclusive end of the offer window as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
  'rrule?': rruleSchema.configure({
    description: 'Optional iCal RRULE body refining which dates the offer is active.',
    examples: ['FREQ=WEEKLY;BYDAY=MO,WE'],
  }),
  'availableDays?': type({
    'weekdays?': type('number[]').configure({
      description: 'ISO weekday numbers the employee is available (1=Mon … 7=Sun). Omit to cover every weekday in the range.',
      examples: [[1, 2, 3, 4, 5]],
    }),
    'specificDates?': type('string[]').configure({
      description: 'Specific ISO calendar dates (YYYY-MM-DD) restricting or supplementing the weekday match.',
      examples: [['2026-04-20', '2026-04-22']],
    }),
  })
    .or('undefined')
    .configure({
      description: 'Optional restriction on which days within the offer window the employee is actually available.',
    }),
  'minDurationDays?': type('number | null').configure({
    description:
      'Minimum claim duration in days for a flexible-window offer. Must be paired with `maxDurationDays`. Omit (or both null) for a fixed-window offer.',
    examples: [7],
  }),
  'maxDurationDays?': type('number | null').configure({
    description:
      'Maximum claim duration in days for a flexible-window offer. Must be paired with `minDurationDays` and >= it. Omit (or both null) for a fixed-window offer.',
    examples: [7],
  }),
  'isSplittable?': type('boolean').configure({
    description:
      'Story 23-11: when false, claimants must take the entire offer window as one continuous block. Defaults to true if omitted.',
    examples: [true],
  }),
  'notes?': type('string | null').configure({
    description: 'Optional free-form notes shown to planners browsing the pool. `null` clears the note.',
    examples: ['Available only mornings.'],
  }),
}).configure({ description: 'Payload for creating a new Labour Pool offer (an employee a team is willing to lend out).' });

export type CreateLabourPoolOffer = typeof createLabourPoolOfferSchema.infer;

// ---------------------------------------------------------------------------
// Query schema — validated on GET /api/v1/labour-pool/offers
// ---------------------------------------------------------------------------

export const labourPoolOfferQuerySchema = type({
  /** Filter by offering team (own team offers) */
  'offeringTeamId?': 'string',
  /** Filter by status; defaults to active */
  'status?': type.enumerated(...labourPoolOfferStatuses),
  /** Filter offers that overlap with this date range */
  'fromDate?': 'string',
  'toDate?': 'string',
  /** Return only offers whose window intersects this request and whose duration/skill constraints are compatible */
  'matchingRequestId?': 'string',
});

export type LabourPoolOfferQuery = typeof labourPoolOfferQuerySchema.infer;
